import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { BulkDownloadFileFilters } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface BulkReissueDocumentsBody {
	allDocumentsSelected: boolean;
	excludedDocumentWorkflowInstanceIds?: string[];
	selectedDocumentWorkflowInstanceIds?: string[];
	filters?: BulkDownloadFileFilters;
	customText?: string;
}

export const useBulkReissueDocuments = ({
	dealId,
	programmeId,
	onSuccess,
	onSettled,
}: {
	dealId: string;
	programmeId: string;
	onSuccess?: () => void;
	onSettled?: () => void;
}) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const endpoint = formatUnicorn(
		'/deal/{dealId}/mip/{programmeId}/document-workflow/bulk-reissue',
		{
			dealId,
			programmeId,
		}
	);

	const handleSuccess = async () => {
		showToastV2({
			title: tMipManage('documents_page.toast.reissue.successful'),
			state: 'success',
		});

		await queryClient.invalidateQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET, {
					dealId,
					programmeId,
				}),
			],
		});

		onSuccess?.();
	};

	const handleError = () => {
		showToastV2({
			state: 'error',
			title: tMipManage('documents_page.toast.reissue.error'),
		});
	};

	return useMutation({
		mutationFn: async (body: BulkReissueDocumentsBody) =>
			await makeRequest<ApiV1.Response<void>>({
				endpoint,
				method: HTTP_METHOD.POST,
				body: JSON.stringify(body),
			}),
		onSuccess: handleSuccess,
		onError: handleError,
		onSettled,
	});
};
